import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "../../../../lib/network/api";
import { AxiosResponse } from "axios";
import { toast } from "sonner";
import { getDecodedJwt } from "../auth";
import { ApiResponse } from "@/src/lib/network/axios";
import { UserData } from "../types/authOnboarding";

interface AdminUserActionResponse {
  message: string;
  statusCode: number;
}

const user = getDecodedJwt();

// Send profile completion reminder
export const sendProfileReminder = async ({
  userId,
}: {
  userId: string;
}): Promise<AdminUserActionResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.post(
    `/admin/users/${userId}/${user?.id}/send-profile-reminder`,
    {}
  );
  return response.data;
};

export const useSendProfileReminder = () => {
  return useMutation({
    mutationFn: sendProfileReminder,
    onSuccess: (data) => {
      toast.success(data.message || "Profile reminder sent successfully");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to send profile reminder"
      );
    },
  });
};

// Send task availability reminder (generic)
export const sendTaskReminder = async ({
  userId,
}: {
  userId: string;
}): Promise<AdminUserActionResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.post(
    `/admin/users/${userId}/${user?.id}/send-task-reminder`,
    {}
  );
  return response.data;
};

export const useSendTaskReminder = () => {
  return useMutation({
    mutationFn: sendTaskReminder,
    onSuccess: (data) => {
      toast.success(data.message || "Task reminder sent successfully");
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to send task reminder"
      );
    },
  });
};

// Suspend user
export const suspendUser = async ({
  userId,
  reason,
}: {
  userId: string;
  reason: string;
}): Promise<AdminUserActionResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.patch(
    `/admin/users/${userId}/${user?.id}/suspend`,
    { reason }
  );
  return response.data;
};

export const useSuspendUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: suspendUser,
    onSuccess: (data) => {
      toast.success(data.message || "User suspended successfully");
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to suspend user");
    },
  });
};

// Terminate user
export const terminateUser = async ({
  userId,
  reason,
}: {
  userId: string;
  reason: string;
}): Promise<AdminUserActionResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.patch(
    `/admin/users/${userId}/${user?.id}/terminate`,
    { reason }
  );
  return response.data;
};

export const useTerminateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: terminateUser,
    onSuccess: (data) => {
      toast.success(data.message || "User terminated successfully");
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to terminate user");
    },
  });
};

// Reactivate user
export const reactivateUser = async ({
  userId,
}: {
  userId: string;
}): Promise<AdminUserActionResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.patch(
    `/admin/users/${userId}/${user?.id}/reactivate`
  );
  return response.data;
};

export const useReactivateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reactivateUser,
    onSuccess: (data) => {
      toast.success(data.message || "User reactivated successfully");
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to reactivate user");
    },
  });
};

export interface DecryptedSSNResponse {
  ssn: string;
  lastFour: string;
}

export const getDecryptedSSN = async (
  userId: string
): Promise<DecryptedSSNResponse> => {
  const response: AxiosResponse<AdminUserActionResponse> = await api.get(
    `/admin/users/${userId}/${user?.id}/ssn`
  );

  const responseData = response.data as any;

  if (responseData.data) {
    return responseData.data;
  }

  return responseData;
};

export const useGetDecryptedSSN = () => {
  return useMutation({
    mutationFn: (userId: string) => getDecryptedSSN(userId),
    onSuccess: () => {
      // Optionally invalidate any relevant queries
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to retrieve SSN");
    },
  });
};

// Function to fetch user details by ID
export const fetchUserDetails = async (id: string): Promise<UserData> => {
  const res = await api.get<ApiResponse<UserData>>(
    `/admin/users/${id}/details`
  );
  return res.data;
};

export const useFetchUserDetails = (id: string) => {
  return useQuery({
    queryKey: ["userDetails", id],
    queryFn: () => fetchUserDetails(id),
    enabled: !!id, // Ensures query runs only when id is available
  });
};
